import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  HttpResponse,
  TicketCategoryTreeDto,
  TicketCategoryTreeNodeDto,
  TicketCategoryTreeNodeUpdateRequest,
  TicketCategoryTreeSettingsDto,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import {
  cleanTreeFromParents,
  findNodeInTree,
  populateTreeWithParents,
  recursivelyDisableNode,
  recursivelyEnableNode,
} from "@/modules/ticket-categories/helpers/tree-helpers";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

type LocationType = TicketCategoryTreeNodeUpdateRequest["locationType"];

export function useEditTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ node, locationType }: { node: TicketCategoryTreeNodeDto; locationType: LocationType }) =>
      api.putTicketCategoryTreeV1(node.id, {
        locationType,
        externalCode: node.externalCode,
        externalSecondaryCode: node.externalSecondaryCode,
        description: node.description,
        name: node.name,
      }),
    onMutate: ({ node, locationType }) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            const foundNode = findNodeInTree(clonedData.nodes, node.id);
            if (foundNode) {
              foundNode.locationType = locationType;

              return { ...oldData, data: clonedData };
            }
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        title: t("page.ticket-category-tree.enable.error"),
        type: "error",
      });
    },
  });
}

export function useEnableTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: (node: TicketCategoryTreeNodeDto) => api.putTicketCategoryTreeEnableV1(node.id),
    onMutate: (node) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            populateTreeWithParents(clonedData.nodes);
            const nodeToEnable = findNodeInTree(clonedData.nodes, node.id);
            if (nodeToEnable) {
              recursivelyEnableNode(nodeToEnable);
            }

            // React-query can't handle recursive structures
            cleanTreeFromParents(clonedData.nodes);

            return { ...oldData, data: clonedData };
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        title: t("page.ticket-category-tree.enable.error"),
        type: "error",
      });
    },
  });
}

export function useDisableTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: (node: TicketCategoryTreeNodeDto) => api.putTicketCategoryTreeDisableV1(node.id),
    onMutate: (node) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            populateTreeWithParents(clonedData.nodes);
            const nodeToEnable = findNodeInTree(clonedData.nodes, node.id);
            if (nodeToEnable) {
              recursivelyDisableNode(nodeToEnable);
            }

            // React-query can't handle recursive structures
            cleanTreeFromParents(clonedData.nodes);

            return { ...oldData, data: clonedData };
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        title: t("page.ticket-category-tree.disable.error"),
        type: "error",
      });
    },
  });
}

export function useUpdateTicketCategorySettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ vveEnabled }: { vveEnabled: boolean }) =>
      api.putTicketCategoryTreeSettingsV1({
        vveEnabled: vveEnabled,
      }),
    onMutate: (data) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeSettingsDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE_SETTINGS(projectId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            clonedData.vveEnabled = data.vveEnabled;

            return {
              ...oldData,
              data: clonedData,
            };
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        title: t("page.ticket-category-tree.update-settings.error"),
        type: "error",
      });
    },
  });
}
